<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TrashAPI from 'dashboard/api/trash';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();

const activeTab = ref('contacts');
const records = ref([]);
const meta = ref({ count: 0, current_page: 1, total_pages: 1 });
const isLoading = ref(false);
// id do registro aguardando o segundo clique de confirmação do hard delete
const pendingDeleteId = ref(null);

const fetchRecords = async (page = 1) => {
  isLoading.value = true;
  pendingDeleteId.value = null;
  try {
    const { data } = await TrashAPI.get({ recordType: activeTab.value, page });
    records.value = data.payload;
    meta.value = data.meta;
  } catch (error) {
    useAlert(t('TRASH.API.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const switchTab = tab => {
  activeTab.value = tab;
  fetchRecords(1);
};

const restoreRecord = async record => {
  try {
    await TrashAPI.restore({ recordType: activeTab.value, id: record.id });
    useAlert(t('TRASH.API.RESTORE_SUCCESS'));
    fetchRecords(meta.value.current_page);
  } catch (error) {
    useAlert(t('TRASH.API.RESTORE_ERROR'));
  }
};

const destroyRecord = async record => {
  // Primeiro clique arma a confirmação; segundo clique executa.
  if (pendingDeleteId.value !== record.id) {
    pendingDeleteId.value = record.id;
    return;
  }

  try {
    await TrashAPI.destroyPermanently({
      recordType: activeTab.value,
      id: record.id,
    });
    useAlert(t('TRASH.API.DESTROY_SUCCESS'));
    fetchRecords(meta.value.current_page);
  } catch (error) {
    useAlert(t('TRASH.API.DESTROY_ERROR'));
  } finally {
    pendingDeleteId.value = null;
  }
};

const recordTitle = record => {
  if (activeTab.value === 'conversations') {
    return `#${record.display_id} — ${record.contact_name || t('TRASH.UNKNOWN_CONTACT')}`;
  }
  return record.name || t('TRASH.UNKNOWN_CONTACT');
};

const recordSubtitle = record => {
  if (activeTab.value === 'conversations') return record.inbox_name || '';
  return record.phone_number || record.email || '';
};

const formatDate = value =>
  value ? new Date(value).toLocaleString('pt-BR') : '';

onMounted(() => fetchRecords());
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <header
      class="flex items-center justify-between flex-shrink-0 gap-4 px-6 py-4 border-b border-n-weak"
    >
      <div class="flex items-center gap-3">
        <span class="i-lucide-trash-2 size-5 text-n-slate-11" />
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ t('TRASH.HEADER') }}
        </h1>
        <span class="text-sm text-n-slate-11">
          {{ t('TRASH.SUBTITLE') }}
        </span>
      </div>
    </header>

    <div class="flex items-center gap-2 px-6 py-3 border-b border-n-weak">
      <button
        v-for="tab in ['contacts', 'conversations']"
        :key="tab"
        class="px-3 py-1.5 text-sm rounded-lg"
        :class="
          activeTab === tab
            ? 'bg-n-slate-3 text-n-slate-12 font-medium'
            : 'text-n-slate-11 hover:bg-n-slate-2'
        "
        @click="switchTab(tab)"
      >
        {{ t(`TRASH.TABS.${tab.toUpperCase()}`) }}
        <span v-if="activeTab === tab">({{ meta.count }})</span>
      </button>
    </div>

    <div
      v-if="isLoading"
      class="flex items-center justify-center flex-1 gap-2 text-n-slate-11"
    >
      <Spinner class="size-5" />
      {{ t('TRASH.LOADING') }}
    </div>

    <div
      v-else-if="!records.length"
      class="flex flex-col items-center justify-center flex-1 gap-2 text-n-slate-11"
    >
      <span class="i-lucide-trash-2 size-8" />
      {{ t('TRASH.EMPTY_STATE') }}
    </div>

    <main v-else class="flex-1 px-6 py-4 overflow-y-auto">
      <ul class="flex flex-col divide-y divide-n-weak">
        <li
          v-for="record in records"
          :key="record.id"
          class="flex items-center justify-between gap-4 py-3"
        >
          <div class="flex flex-col min-w-0 gap-0.5">
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ recordTitle(record) }}
            </span>
            <span class="text-xs truncate text-n-slate-11">
              {{ recordSubtitle(record) }}
            </span>
            <span class="text-xs text-n-slate-10">
              {{ t('TRASH.DISCARDED_AT') }}: {{ formatDate(record.discarded_at) }}
            </span>
          </div>
          <div class="flex items-center flex-shrink-0 gap-2">
            <Button
              :label="t('TRASH.ACTIONS.RESTORE')"
              color="blue"
              size="sm"
              @click="restoreRecord(record)"
            />
            <Button
              :label="
                pendingDeleteId === record.id
                  ? t('TRASH.ACTIONS.CONFIRM_DESTROY')
                  : t('TRASH.ACTIONS.DESTROY')
              "
              color="ruby"
              size="sm"
              @click="destroyRecord(record)"
            />
          </div>
        </li>
      </ul>

      <div
        v-if="meta.total_pages > 1"
        class="flex items-center justify-center gap-3 py-4 text-sm text-n-slate-11"
      >
        <button
          :disabled="meta.current_page <= 1"
          class="disabled:opacity-40 hover:text-n-slate-12"
          @click="fetchRecords(meta.current_page - 1)"
        >
          ← {{ t('TRASH.PAGINATION.PREVIOUS') }}
        </button>
        <span>{{ meta.current_page }} / {{ meta.total_pages }}</span>
        <button
          :disabled="meta.current_page >= meta.total_pages"
          class="disabled:opacity-40 hover:text-n-slate-12"
          @click="fetchRecords(meta.current_page + 1)"
        >
          {{ t('TRASH.PAGINATION.NEXT') }} →
        </button>
      </div>
    </main>
  </div>
</template>
